import { useNavigate, useParams } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import {
  type PatientSchema,
  patientSchema,
  SampleSchema,
  HPOSchema,
} from '@/zod_schemas';
import {
  useCreatePatient,
  useGetPatient,
  usePatchPatient,
} from '@/api/patient/patient';
import { useGetPatientHPO } from '@/api/hpo';
import { useGetPatientSample } from '@/api/sample/sample';

type PatientPathParams = {
  patientId: string;
};

interface UsePatientFormHandlerReturn
  extends ReturnType<typeof useForm<PatientSchema>> {
  mode: 'create' | 'update';
  patientId?: string;
  defaultHPOs?: HPOSchema[];
  defaultSamples?: SampleSchema[];
}

type UsePatientFormHandler = () => UsePatientFormHandlerReturn;

export const usePatientFormHandler: UsePatientFormHandler = () => {
  const { patientId } = useParams<PatientPathParams>();
  const { data: patientData } = useGetPatient(patientId);
  const { data: hpoDataWrapper } = useGetPatientHPO(patientId);
  const { data: sampleDataWrapper } = useGetPatientSample(patientId);

  const defaultFormValues: PatientSchema = {
    name: '',
    gender: 'unknown',
    identification: '',
    ethnicity: [],
    date_of_birth: '',
    patient_samples: [],
    patient_hpos: [],
  };

  const formMethods = useForm<PatientSchema>({
    resolver: zodResolver(patientSchema),
    defaultValues: defaultFormValues, // Use defaultValues here
  });

  // 👇 Add useEffect to reset form when data changes
  useEffect(() => {
    if (patientId && patientData) {
      console.log('masukl');
      const data: PatientSchema = {
        ...patientData,
        patient_samples: sampleDataWrapper || [],
        patient_hpos: hpoDataWrapper || [],
      };
      formMethods.reset(data);
    }
  }, [patientId, patientData, hpoDataWrapper, sampleDataWrapper, formMethods]);

  return {
    ...formMethods,
    mode: patientId ? 'update' : 'create',
    patientId,
    defaultHPOs: hpoDataWrapper,
    defaultSamples: sampleDataWrapper,
  };
};

export type ProcessedPatientData = Omit<
  PatientSchema,
  'sampleGridData' | 'hpoGridData'
> & {
  samples: SampleSchema[];
  hpos: HPOSchema[];
};
// const ethnicityData = data.map((item) => item.name);

// processPatientData.js or similar
export const processPatientData = (data: PatientSchema) => {
  const processedData = {
    ...data,
    sample_ids: data.patient_samples
      ? data.patient_samples
          .map((item) => item.id)
          .filter((id): id is string => id !== undefined)
      : [],
    hpo_ids: data.patient_hpos
      ? data.patient_hpos
          .map((item) => item.hpo_id)
          .filter((id): id is string => id !== undefined)
      : [],
  };

  return processedData;
};

export const useCreateOrUpdatePatient = (
  mode: 'create' | 'update',
  patientId?: string,
) => {
  const navigate = useNavigate();
  const createPatient = useCreatePatient();
  const updatePatient = usePatchPatient();

  const onSubmit: SubmitHandler<PatientSchema> = useCallback(
    (data) =>
      new Promise<void>((resolve, reject) => {
        // Process the patient data before submitting
        const processedData = processPatientData(data);
        if (mode === 'create') {
          console.log('mode', mode);
          createPatient
            .mutateAsync(processedData)
            .then(({ data: { id } }) => {
              resolve();

              enqueueSnackbar('Patient created successfully', {
                variant: 'success',
              });

              navigate(`/patients/${id}`);
            })
            .catch((ex) => {
              reject(ex);

              enqueueSnackbar('Failed to create patient', {
                variant: 'error',
              });
            });
        } else {
          if (!patientId) {
            reject(new Error('Patient ID is required'));

            enqueueSnackbar('Unknown error occurred', {
              variant: 'error',
            });

            return;
          }

          updatePatient
            .mutateAsync({ payload: data, id: patientId })
            .then(() => {
              resolve();

              enqueueSnackbar('Patient updated successfully', {
                variant: 'success',
              });

              navigate(`/patients/${processedData.id}`);
            })
            .catch((ex) => {
              reject(ex);

              enqueueSnackbar('Failed to update patient', {
                variant: 'error',
              });
            });
        }
      }),
    [createPatient, mode, navigate, patientId, updatePatient],
  );

  // const onSubmit: SubmitHandler<PatientSchema> = (data) => {
  //   console.log(data);
  //   const processedData = processPatientData(data, sampleGridData, hpoGridData);
  //   console.log(processedData);
  // };
  return onSubmit;
};
